import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SmlsInputText, SmlsListItem } from '@smiles/smiles-ui-kit-react';
import { useTranslation } from 'react-i18next';
import { isValidEmail } from 'pages/RedoRegister/helpers';
import { useMemberData } from 'pages/RedoRegisterTangie/context/MemberDataContext';
import { useEmailSharedValidation } from 'pages/RedoRegisterTangie/context/EmailSharedValidationContext';
import { useEmailRecaptcha } from 'pages/RedoRegisterTangie/context/EmailRecaptchaContext';

interface SuggestedEmails {
  label: string;
  html: React.JSX.Element;
}
export function EmailInput() {
  const domainsValid = ['gmail.com', 'hotmail.com', 'outlook.com', 'uol.com.br'];

  const { t } = useTranslation('redo-register-v2');

  const { handler, response, data, setFormEdit, getMemberData } = useMemberData();
  const { emailInput, confirmEmailInput } = useEmailSharedValidation();
  const recaptcha = useEmailRecaptcha();

  const [value, setValue] = useState('');
  const [suggestedEmails, setSuggestedEmails] = useState<SuggestedEmails[]>([]);

  const thisRef = useRef<HTMLUListElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = e.target.value;
    const atIndex = eventValue.split('@');
    const name = atIndex[0];
    const domainUser = atIndex[1];
    const isLongEmail = eventValue.length > 30;
    const isStartingDomain = eventValue.endsWith('@');

    handler({ email: '', confirmEmail: '' });

    confirmEmailInput.resetInputValue();
    confirmEmailInput.resetError();
    emailInput.resetError();

    if (recaptcha.enableRecaptcha && !recaptcha.isVisible) {
      recaptcha.showRecaptcha();
    }

    setValue(eventValue);
    handler({ email: eventValue });
    setFormEdit();

    const textResult = (domain: string) => {
      const htmlStr = domain.replace(domainUser, `<b>${domainUser}</b>`);
      return (
        <>
          @
          <span dangerouslySetInnerHTML={{ __html: htmlStr }} />
        </>
      );
    };

    const truncatedResult = (emailName: string, domain: string) => {
      const htmlStr = domain.replace(domainUser, `<b>${domainUser}</b>`);

      const truncatedName = `...${emailName.substring(emailName.length - 25)}`;
      const htmlName = `<b>${truncatedName}</b>`;
      return (
        <>
          <span dangerouslySetInnerHTML={{ __html: htmlName }} />
          @
          <span dangerouslySetInnerHTML={{ __html: htmlStr }} />
        </>
      );
    };

    const result = domainsValid
      .filter((domain) => domain.toLocaleLowerCase().includes(domainUser?.toLocaleLowerCase()))
      .map((domain) => {
        if (isLongEmail && isStartingDomain) {
          return {
            label: `${name}@${domain}`,
            html: truncatedResult(name, domain),
          };
        }

        return {
          label: `${name}@${domain}`,
          html: textResult(domain),
        };
      });

    setSuggestedEmails(result);
  };

  const handleValidate = useCallback(() => {
    if (getMemberData.email === value) {
      return true;
    }

    const result = isValidEmail(value);

    if (!result) {
      handler({ email: '' });
      return result;
    }

    handler({ email: value });
    return result;
  }, [value]);

  const handleOnDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    return false;
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    return false;
  };

  const handleDomainsValidate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeout(() => {
      setSuggestedEmails([]);
    }, 200);
  };

  useEffect(() => {
    if (!response.ready) {
      return;
    }

    if (response.ready && getMemberData.email === 'mudar@mudar.com.br') {
      setValue('');
      return;
    }

    setValue(data.email);
  }, [response]);

  const errorMessage = emailInput.forceError ? t(emailInput.errorMessage) : t('errors.invalid_email');

  return (
    <div className="input-group--content" data-testid="first-access-email-input-container">
      <SmlsInputText
        id="first-access-email-input"
        data-testid="first-access-email-input"
        autoComplete="off"
        placeholder={t('placeholders.email')}
        value={value}
        maxLength={100}
        errorMessage={errorMessage}
        defaultValue=""
        regex={/[^.a-zA-Z0-9@_-]+|@(.*)@/g}
        debouncedTime={700}
        inputMode="email"
        textLabel={t('labels.email')}
        validate={handleValidate}
        onChange={handleChange}
        onDrop={handleOnDrop}
        onPaste={handleOnPaste}
        onBlur={handleDomainsValidate}
        forceError={false}
      />
      <ul className="dropdown-menu show" ref={thisRef} data-testid="redo-register-email-input-suggestion">
        {suggestedEmails.map((suggest, index) => (
          <SmlsListItem
            id={`item_${index}`}
            item={suggest.html as unknown as string}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            onClick={() => {
              setValue(suggest.label);
              setSuggestedEmails([]);
            }}
          />
        ))}
      </ul>
    </div>
  );
}
